import { BLOG_PAGE_SIZE } from "./pagination-config.js";

export { BLOG_PAGE_SIZE };

/**
 * Total number of archive pages for `n` eligible posts.
 *
 * Always returns at least 1 so the build still generates an empty
 * `/blog/` index page when no posts are eligible.
 */
export function computeTotalPages(n: number): number {
  if (!Number.isFinite(n) || n <= 0) return 1;
  return Math.max(1, Math.ceil(n / BLOG_PAGE_SIZE));
}

export type ParseArchivePageResult =
  | { kind: "ok"; page: number }
  | { kind: "page-one-alias" }
  | { kind: "invalid" };

/**
 * Resolve a raw `:page` segment from `/blog/page/:page`.
 *
 * - `'1'`           → `page-one-alias` (Netlify also 301s the document)
 * - integers in `[2..totalPages]` → `ok`
 * - everything else → `invalid`  (component renders the NotFound element;
 *                                  real document hits get the Netlify
 *                                  static 404 page because no archive
 *                                  HTML file exists at that path)
 */
export function parseArchivePage(
  raw: string | null | undefined,
  totalPages: number,
): ParseArchivePageResult {
  if (raw == null) return { kind: "invalid" };
  if (!/^[0-9]+$/.test(raw)) return { kind: "invalid" };
  const n = Number.parseInt(raw, 10);
  if (n === 1) return { kind: "page-one-alias" };
  if (n >= 2 && n <= totalPages) return { kind: "ok", page: n };
  return { kind: "invalid" };
}

/** Canonical archive path for a given page number (no `?page=` ever). */
export function archivePagePath(page: number): string {
  if (page <= 1) return "/blog/";
  return `/blog/page/${page}/`;
}

/** Match a pathname against the path-based archive routes. */
export function pathnameToArchivePage(pathname: string): {
  isArchive: boolean;
  rawPage: string | null;
} {
  // `/blog`, `/blog/`           → page 1
  // `/blog/page/<n>` (+ slash)  → page <n>
  if (/^\/blog\/?$/.test(pathname)) {
    return { isArchive: true, rawPage: null };
  }
  const m = pathname.match(/^\/blog\/page\/([^/]+)\/?$/);
  if (m) return { isArchive: true, rawPage: m[1] };
  return { isArchive: false, rawPage: null };
}